import { FC, useReducer, useState } from 'react';
import { CampaignCategoryFormMode, CampaignCategoryItem } from '../../../../types/campaign-category';
import { useCommonStyles } from '../../../../../../common/styles/common-styles';
import { makeStyles } from 'tss-react/mui';
import { Button, TextField, FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { CONFIG_BASE_PATH, CONFIG_CAMPAIGN_CATEGORY_PATH, MANDATORY_FIELD_ERROR_TEXT } from '../../../../constants';
import { useIntl } from 'react-intl';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { useDispatch } from 'react-redux';
import { createCampaignCategory, modifyCampaignCategory } from '../../../../network/campaignCategoryCrud';

type CampaignCategoryFormFormProps = {
  code?: string;
  formMode: CampaignCategoryFormMode;
  campaignCategory?: CampaignCategoryItem;
  onReload: () => void;
  onRouteTo: (route: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  fieldContainer: {
    width: 160,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  sectionMargin: {
    marginBottom: 20,
  },
  divideMargin: {
    marginBottom: 10,
  },
  subHeader: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  newsText: {
    lineHeight: 1.8,
  },
  imgRow: {
    marginTop: 20,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  imgItem: {
    margin: 10,
    maxWidth: 500,
  },
  tag: {
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: 12,
  },
  inputMargin: {
    marginRight: 20,
  },
  mandatory: {
    color: 'red',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 10,
    color: '#f018a6',
    paddingLeft: 14,
  },
  settingTable: {
    width: '100%',
  },
  checkboxNoArchiveContainer: {
    margin: 0,
  },
  checkboxFieldContainer: {
    paddingTop: 9,
    minWidth: 160,
    boxSizing: 'border-box',
  },
  textAreaRowContainer: {
    display: 'flex',
  },
  dialogContainer: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogTitle: {
    fontSize: 20,
  },
  btnContainer: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButton: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '5px',
  },
  thumbnails: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbnailContainer: {
    backgroundColor: 'red',
    padding: '10px',
    marginLeft: 10,
    marginRight: 20,
    marginBottom: 10,
    borderRadius: 5,
    height: 50,
    width: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbnailImage: {
    width: 'auto',
    height: 'auto',
  },
  checkBoxContainer: {
    padding: '10px',
    marginLeft: 5,
    marginRight: 20,
    marginBottom: 10,
    borderRadius: 5,
    height: 50,
    width: 50,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export type CampaignCategoryFormState = {
  _id: string;
  categoryNameHK: string;
  categoryNameEn: string;
  isDefault: boolean;
  order: number;
  active: boolean;
};

const initialState: CampaignCategoryFormState = {
  _id: '',
  categoryNameHK: '',
  categoryNameEn: '',
  isDefault: false,
  order: 1,
  active: false,
};

const detailToStateConvertor = (detail: CampaignCategoryItem): CampaignCategoryFormState => {
  return {
    _id: detail._id,
    categoryNameHK: detail.categoryNameHK,
    categoryNameEn: detail.categoryNameEn,
    isDefault: detail.isDefault,
    order: detail.order,
    active: detail.active,
  };
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof CampaignCategoryFormState;
    value: any;
  };
};

type AgentSegmentFormAction = ModifyFieldAction;

const agentSegmentFormReducer = (
  state: CampaignCategoryFormState,
  action: AgentSegmentFormAction,
): CampaignCategoryFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
  }
};

const CampaignCategoryForm: FC<CampaignCategoryFormFormProps> = ({ campaignCategory, code, formMode, onRouteTo }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);
  const [formState, formDispatch] = useReducer(
    agentSegmentFormReducer,
    campaignCategory ? detailToStateConvertor(campaignCategory) : initialState,
  );
  const [errorCategoryNameCn, setErrorCategoryNameCn] = useState<string>();
  const [errorCategoryNameEn, setErrorCategoryNameEn] = useState<string>();

  const validForm = () => {
    let flag = false;
    if (!formState.categoryNameHK || formState.categoryNameHK.trim() === '') {
      flag = true;
      setErrorCategoryNameCn(MANDATORY_FIELD_ERROR_TEXT);
    }
    if (!formState.categoryNameEn || formState.categoryNameEn.trim() === '') {
      flag = true;
      setErrorCategoryNameEn(MANDATORY_FIELD_ERROR_TEXT);
    }
    return flag;
  };

  const onSubmitForm = async () => {
    const valid = validForm();
    if (!valid) {
      // Call Api and submit
      try {
        const details: CampaignCategoryItem = {
          _id: formState._id,
          categoryNameHK: formState.categoryNameHK,
          categoryNameEn: formState.categoryNameEn,
          isDefault: formState.isDefault,
          order: formState.order || 1,
          active: formState.active,
        };

        if (formMode === CampaignCategoryFormMode.CREATE) {
          await createCampaignCategory(details, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: Translation('configCampaignCategory.success.saved'),
              },
            ]),
          );
        } else if (code && formMode === CampaignCategoryFormMode.EDIT) {
          await modifyCampaignCategory(code, details, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: Translation('configCampaignCategory.success.updated', { code }),
              },
            ]),
          );
        }
        onRouteTo(`${CONFIG_BASE_PATH}${CONFIG_CAMPAIGN_CATEGORY_PATH}`);
      } catch (err) {}
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>
            {formMode === CampaignCategoryFormMode.CREATE
              ? Translation('configCampaignCategory.newCategory')
              : Translation('configCampaignCategory.edit')}
          </div>
        </div>
        <Button
          variant="contained"
          color="inherit"
          onClick={() => {
            history.push(`${CONFIG_BASE_PATH}${CONFIG_CAMPAIGN_CATEGORY_PATH}`);
          }}
        >
          {Translation('app.button.back')}
        </Button>
      </div>
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}></div>
        <div key={'categoryNameHKDiv'} className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation(`configCampaignCategory.common.categoryNameHK`)}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              margin="dense"
              variant="outlined"
              fullWidth
              error={errorCategoryNameCn ? true : false}
              helperText={errorCategoryNameCn}
              value={formState.categoryNameHK || ''}
              maxRows={12}
              onChange={(e) => {
                const regName = new RegExp(/^.{0,12}$/, 'i');
                if (!regName.test(e.target.value)) {
                  setErrorCategoryNameCn(Translation(`configCampaignCategory.common.category.error`));
                } else {
                  setErrorCategoryNameCn('');
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'categoryNameHK', value: e.target.value },
                  });
                }
              }}
            />
          </div>
        </div>
        <div key={'categoryNameENDiv'} className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation(`configCampaignCategory.common.categoryNameEn`)}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              margin="dense"
              variant="outlined"
              fullWidth
              error={errorCategoryNameEn ? true : false}
              helperText={errorCategoryNameEn}
              value={formState.categoryNameEn || ''}
              maxRows={12}
              onChange={(e) => {
                const regName = new RegExp(/^.{0,12}$/, 'i');
                if (!regName.test(e.target.value)) {
                  setErrorCategoryNameEn(Translation(`configCampaignCategory.common.category.error`));
                } else {
                  setErrorCategoryNameEn('');
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'categoryNameEn', value: e.target.value },
                  });
                }
              }}
            />
          </div>
        </div>
        <div key={'orderDiv'} className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation(`configCampaignCategory.common.order`)}:</span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              margin="dense"
              variant="outlined"
              fullWidth
              value={formState.order}
              onChange={(e) => {
                const regName = new RegExp(/^\d+$/);
                if (regName.test(e.target.value)) {
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'order', value: parseInt(e.target.value) },
                  });
                } else {
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'order', value: '' },
                  });
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* enable is default button */}
      <div className={classes.rowContainer}>
        <div className={classes.fieldContainer}>
          <span className={classes.field}>{Translation('configCampaignCategory.common.isDefault')}:</span>
        </div>
        <div style={{ width: '75%' }}>
          <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
            <FormControlLabel
              value="Enable"
              control={<Radio disabled={false} />}
              label={Translation('configAgentSegment.common.enable')}
              checked={formState.isDefault === true}
              onChange={() => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isDefault', value: true } });
              }}
            />
            <FormControlLabel
              value="Disable"
              control={<Radio disabled={false} />}
              checked={formState.isDefault === false}
              label={Translation('configAgentSegment.common.disable')}
              onChange={async () => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isDefault', value: false } });
              }}
            />
          </RadioGroup>
        </div>
      </div>
      <div className={classes.rowContainer}>
        <div className={classes.fieldContainer}>
          <span className={classes.field}>{Translation('configCampaignCategory.common.status')}:</span>
        </div>
        <div style={{ width: '75%' }}>
          <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
            <FormControlLabel
              value="Enable"
              control={<Radio disabled={false} />}
              label={Translation('configAgentSegment.common.enable')}
              checked={formState.active === true}
              onChange={() => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'active', value: true } });
              }}
            />
            <FormControlLabel
              value="Disable"
              control={<Radio disabled={false} />}
              checked={formState.active === false}
              label={Translation('configAgentSegment.common.disable')}
              onChange={async () => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'active', value: false } });
              }}
            />
          </RadioGroup>
        </div>
      </div>

      <div className={classes.footerContainer}>
        {
          <>
            <Button style={{ marginLeft: 25 }} variant="contained" color="secondary" onClick={() => onSubmitForm()}>
              {Translation('app.button.submit')}
            </Button>
          </>
        }
      </div>
    </div>
  );
};

export default CampaignCategoryForm;
